/**
 * Shared helpers for analytics line charts. Buckets events by UTC
 * calendar day (same as UniqueVisitorsCard / SharingActivityCard) and
 * zero-fills every day in the selected period so the X axis always
 * matches the date range at the top of the page.
 */

/**
 * Match AnalyticsTwo dateParams windows (24h / 7d / 30d).
 * @param {number} [refreshTick] - Sync counter; when it changes, `to` is
 *   recomputed to the current time without altering the window length.
 */
export const chartPeriodBoundsForTimeRange = (timeRange, refreshTick = 0) => {
  void refreshTick;
  const to = new Date();
  const from = new Date();
  if (timeRange === '24h') from.setHours(from.getHours() - 24);
  else if (timeRange === '7d') from.setDate(from.getDate() - 7);
  else if (timeRange === '30d') from.setDate(from.getDate() - 30);
  else from.setDate(from.getDate() - 7);
  return { from, to };
};

/** UTC day keys (YYYY-MM-DD) from the start of `from`'s day through `to`'s day. */
export const eachUtcDayKeyInRange = (periodFrom, periodTo) => {
  if (!periodFrom || !periodTo) return [];
  const keys = [];
  const cursor = new Date(periodFrom);
  cursor.setUTCHours(0, 0, 0, 0);
  const end = new Date(periodTo);
  end.setUTCHours(0, 0, 0, 0);
  while (cursor <= end) {
    keys.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return keys;
};

/** Long label for tooltips, e.g. "May 27". */
export const formatUtcDayLabel = (utcDayKey) => {
  if (!utcDayKey) return '';
  const d = new Date(`${utcDayKey}T12:00:00.000Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Compact chart-axis label, e.g. "5/27" — narrower so ticks space evenly. */
export const formatUtcDayLabelCompact = (utcDayKey) => {
  if (!utcDayKey) return '';
  const d = new Date(`${utcDayKey}T12:00:00.000Z`);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
};

/**
 * Evenly spaced tick indices for a series of `pointCount` points.
 * Always includes first and last index when pointCount > 1.
 */
export const buildEvenChartTickIndices = (pointCount, maxTicks = 5) => {
  if (pointCount <= 0) return [];
  if (pointCount <= maxTicks) {
    return Array.from({ length: pointCount }, (_, i) => i);
  }
  const indices = [0];
  const innerSlots = maxTicks - 2;
  const step = (pointCount - 1) / (innerSlots + 1);
  for (let i = 1; i <= innerSlots; i += 1) {
    indices.push(Math.round(i * step));
  }
  indices.push(pointCount - 1);
  return [...new Set(indices)].sort((a, b) => a - b);
};

/**
 * Label values for Recharts XAxis `ticks` — evenly spaced, compact dates.
 * @param {{ label: string }[]} timeseries
 * @param {{ maxTicks?: number }} [options]
 */
export const buildChartXAxisTickLabels = (timeseries, { maxTicks = 5 } = {}) => {
  if (!Array.isArray(timeseries) || timeseries.length === 0) return [];
  const indices = buildEvenChartTickIndices(timeseries.length, maxTicks);
  return indices
    .map((i) => timeseries[i]?.label)
    .filter((label) => label != null && label !== '');
};

/** Plain copy when we cannot draw an honest daily trend (feed event cap). */
export const CHART_TREND_UNAVAILABLE =
  'Daily trend is unavailable for very busy periods. Your total above is still for the dates you selected.';
